import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
/// <reference types="@types/google.maps" />
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  CircularProgress,
  List,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Container,
  Card,
  CardContent,
  IconButton,
  useTheme,
  Slider,
  keyframes,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import ExploreIcon from '@mui/icons-material/Explore';
import TourIcon from '@mui/icons-material/Tour';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TourMap from './TourMap';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CategoryIcon from '@mui/icons-material/Category';
import StarsIcon from '@mui/icons-material/Stars';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Footer from './Footer';
import AppHeader from './AppHeader';
import WelcomePopup from './WelcomePopup';
import InfoDialog from './InfoDialog';
import { useAuth } from '../contexts/AuthContext';
import { saveTourForUser } from '../services/savedTours';
import { resolveStopCoordinates, resolveTourCityContext, waitForGoogleMaps } from '../services/placeLookup';
import { parseTourLocation, stripParentheticals } from '../utils/tourLocation';
import About from '../pages/About';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import { sampleTours } from '../data/sampleTours';

interface TourStop {
  pointOfInterest: string;
  location: string;
  description: string;
  timeSpent: string;
  walkingDirections?: string;
  funFacts: string[];
  coordinates?: {
    lat: number;
    lng: number;
  };
  thumbnailUrl?: string;
}

interface TourResponse {
  tourTitle: string;
  duration: string;
  tourDetails: {
    pointOfInterest: string;
    location: string;
    locationDetails?: {
      placeName: string;
      streetAddress: string;
      city: string;
      state: string;
      country: string;
    };
    description: string;
    timeSpent: string;
    walkingDirections: string;
    funFacts: string[];
    coordinates: {
      lat: number;
      lng: number;
    } | null;
    thumbnailUrl?: string;
  }[];
}

const formatPlainTextAnswer = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .trim();

const sanitizeTourResponse = (tour: TourResponse, tourCity = ''): TourResponse => ({
  ...tour,
  tourTitle: stripParentheticals(tour.tourTitle),
  tourDetails: tour.tourDetails.map((detail) => {
    const parsed = detail.locationDetails
      ? {
          location: stripParentheticals(detail.location),
          locationDetails: {
            placeName: stripParentheticals(detail.locationDetails.placeName || detail.pointOfInterest),
            streetAddress: stripParentheticals(detail.locationDetails.streetAddress),
            city: stripParentheticals(detail.locationDetails.city || tourCity),
            state: stripParentheticals(detail.locationDetails.state),
            country: stripParentheticals(detail.locationDetails.country),
          },
        }
      : parseTourLocation(detail.location, detail.pointOfInterest, tourCity);

    return {
      ...detail,
      pointOfInterest: stripParentheticals(detail.pointOfInterest),
      location: parsed.location,
      locationDetails: parsed.locationDetails,
    };
  }),
});

const THEME = {
  colors: {
    primary: '#FF6B6B',  // Coral pink
    secondary: '#4ECDC4', // Mint
    accent1: '#FFE66D',  // Sunny yellow
    accent2: '#96CEB4',  // Sage green
    accent3: '#FFEEAD', // Soft peach
    accent4: '#FF9999', // Light coral
    text: '#363636',    // Soft black
    background: '#FFFFFF'
  }
};

const walkingAnimation = keyframes`
  0% {
    transform: translateX(-50%) translateY(0px);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  45% {
    transform: translateX(50%) translateY(0px);
    opacity: 1;
  }
  50% {
    opacity: 0;
    transform: translateX(50%) translateY(0px);
  }
  51% {
    transform: translateX(-50%) translateY(0px);
  }
  55% {
    opacity: 1;
  }
  95% {
    transform: translateX(50%) translateY(0px);
    opacity: 1;
  }
  100% {
    transform: translateX(50%) translateY(0px);
    opacity: 0;
  }
`;

const mapPattern = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 40px 40px;
  }
`;

const loadingTexts = [
  "Exploring local secrets...",
  "Crafting the perfect route...",
  "Adding interesting stories...",
  "Finding hidden gems...",
  "Planning photo spots...",
  "Adding local tips...",
  "Mapping scenic paths..."
];

const textAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  20% {
    opacity: 0;
    transform: translateY(-8px);
  }
  25% {
    opacity: 0;
    transform: translateY(8px);
  }
  35% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TOUR_THEMES = [
  { value: 'top-hits', label: 'Top Hits', description: 'Visit the most iconic and must-see attractions' },
  { value: 'historical', label: 'Historical', description: 'Discover the city\'s rich past and landmarks' },
  { value: 'modern', label: 'Modern', description: 'Experience contemporary architecture and culture' },
  { value: 'arts', label: 'Arts & Culture', description: 'Explore galleries, museums, and cultural spots' },
  { value: 'nature', label: 'Nature', description: 'Visit parks, gardens, and scenic outdoor locations' },
  { value: 'hidden-gems', label: 'Hidden Gems', description: 'Uncover local secrets and off-the-beaten-path spots' },
  { value: 'photo-spots', label: 'Photo Spots', description: 'Find the most Instagram-worthy locations' }
] as const;

const loadGoogleMapsScript = (callback: () => void) => {
  // Check if script is already loaded
  if (window.google) {
    callback();
    return;
  }

  // Check if script is already being loaded
  const existingScript = document.getElementById('google-maps-script');
  if (existingScript) {
    existingScript.addEventListener('load', callback);
    return;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'google-maps-script';
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  script.onerror = (error) => {
    console.error('Failed to load Google Maps script:', error);
  };
  document.head.appendChild(script);
};

const fetchWikipediaImage = async (pointOfInterest: string, city: string): Promise<string | null> => {
  try {
    // Remove "the" from the beginning of the title and clean for URL
    const searchTerm = pointOfInterest
      .replace(/^the\s+/i, '')  // Remove "the" from the beginning (case insensitive)
      .replace(/\s+/g, ' ');    // Normalize spaces
    
    // First, search for the best matching article with city context
    const searchResponse = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(`${searchTerm} ${city}`)}&format=json&origin=*`
    );
    const searchData = await searchResponse.json();
    
    if (!searchData.query?.search?.length) {
      // Try searching without city context if no results found
      const fallbackResponse = await fetch(
        `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchTerm)}&format=json&origin=*`
      );
      const fallbackData = await fallbackResponse.json();
      
      if (!fallbackData.query?.search?.length) {
        return null;
      }
      
      // Use the fallback search results
      const bestMatch = fallbackData.query.search[0];
      const exactTitle = bestMatch.title.replace(/\s+/g, '_');
      
      // Now get the summary with the exact title
      const summaryResponse = await fetch(
        `https://en.wikipedia.org/api/rest_v1/page/summary/${exactTitle}`
      );
      const summaryData = await summaryResponse.json();
      
      if (summaryData.thumbnail && summaryData.thumbnail.source) {
        return summaryData.thumbnail.source;
      }
      
      return null;
    }
    
    // Get the title of the best matching article
    const bestMatch = searchData.query.search[0];
    const exactTitle = bestMatch.title.replace(/\s+/g, '_');
    
    // Now get the summary with the exact title
    const summaryResponse = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${exactTitle}`
    );
    const summaryData = await summaryResponse.json();
    
    if (summaryData.thumbnail && summaryData.thumbnail.source) {
      return summaryData.thumbnail.source;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

type DialogPageKey = 'about' | 'privacy' | 'terms' | null;

const TourForm: React.FC = () => {
  const { tourSlug } = useParams<{ tourSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, openAuthDialog } = useAuth();
  const muiTheme = useTheme();
  const [city, setCity] = useState('');
  const [duration, setDuration] = useState('');
  const [durationError, setDurationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [itinerary, setItinerary] = useState<TourResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<{ [key: number]: string }>({});
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [loadingAnswers, setLoadingAnswers] = useState<{ [key: number]: boolean }>({});
  const [speaking, setSpeaking] = useState<{ [key: number]: boolean }>({});
  const [voiceSpeed, setVoiceSpeed] = useState<{ [key: number]: number }>({});
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [selectedStop, setSelectedStop] = useState<number | null>(null);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [showWelcomePopup, setShowWelcomePopup] = useState<boolean>(false);
  const [selectedSampleTour, setSelectedSampleTour] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogPageKey, setDialogPageKey] = useState<DialogPageKey>(null);
  const [feedback, setFeedback] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [savingTour, setSavingTour] = useState(false);
  const [tourSaved, setTourSaved] = useState(false);
  const [enrichingTour, setEnrichingTour] = useState(false);
  const enrichmentRunId = useRef(0);
  const shouldScrollToTourRef = useRef(false);
  const tourResultsRef = useRef<HTMLDivElement | null>(null);

  // Sort sample tours alphabetically by displayTitle
  const sortedSampleTours = useMemo(() => {
    return [...sampleTours].sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));
  }, []);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    const loadedTour = (location.state as {
      loadedTour?: TourResponse & { city?: string | null; theme?: string | null };
    })?.loadedTour;

    if (loadedTour) {
      setItinerary(sanitizeTourResponse({
        tourTitle: loadedTour.tourTitle,
        duration: loadedTour.duration,
        tourDetails: loadedTour.tourDetails,
      }, loadedTour.city || city));
      if (loadedTour.city) setCity(loadedTour.city);
      if (loadedTour.theme) setSelectedTheme(loadedTour.theme);
      setTourSaved(true);
      setError(null);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!itinerary || !shouldScrollToTourRef.current) {
      return;
    }

    shouldScrollToTourRef.current = false;

    requestAnimationFrame(() => {
      const tourElement = tourResultsRef.current;
      if (!tourElement) {
        return;
      }

      const headerOffset = 80;
      const top = tourElement.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }, [itinerary]);

  useEffect(() => {
    loadGoogleMapsScript(() => setIsMapLoaded(true));

    return () => {
      // No need to remove the script on unmount as it might be needed by other components
      setIsMapLoaded(false);
    };
  }, []);

  // Update effect for cycling loading text
  useEffect(() => {
    if (!loading) return;
    
    const interval = setInterval(() => {
      setLoadingTextIndex(prev => (prev + 1) % loadingTexts.length);
    }, 3000); // Increased from 2000 to 3000 to match animation duration

    return () => clearInterval(interval);
  }, [loading]);

  // Effect to load tour from URL slug on initial mount
  useEffect(() => {
    // console.log('Mount effect running. tourSlug:', tourSlug); // Debug log
    if (tourSlug) {
      // Find tour matching the slug (case-insensitive)
      const matchedTour = sampleTours.find(
        tour => tour.slug.toLowerCase() === tourSlug.toLowerCase()
      );

      if (matchedTour) {
        // console.log('Found matching tour:', matchedTour.displayTitle); // Debug log
        setItinerary(matchedTour.itinerary as TourResponse);
        setSelectedSampleTour(matchedTour.displayTitle); // Pre-select dropdown
        // Clear generation inputs
        setCity('');
        setDuration('');
        setSelectedTheme('');
        setError(null);
        setLoading(false);
      } else {
        // console.warn(`Tour slug '${tourSlug}' not found in sample tours.`);
        setError(`Pre-made tour for "${tourSlug}" not found.`);
      }
    } else {
      // console.log('No tourSlug found in URL.'); // Debug log
    }
  }, [tourSlug]); // <-- Added tourSlug to dependency array

  // Effect to check for welcome popup (runs after URL check)
  useEffect(() => {
    // Only check for welcome popup if no itinerary is loaded (either from URL or state)
    if (!itinerary) { 
      const hasVisited = localStorage.getItem('hasVisitedWTG');
      if (!hasVisited) {
        setShowWelcomePopup(true);
      }
    }
  }, [itinerary]); // Re-check if itinerary state changes

  const handleSpeak = (index: number, text: string) => {
    if (speaking[index]) {
      window.speechSynthesis.cancel();
      setSpeaking(prev => ({ ...prev, [index]: false }));
      return;
    }

    window.speechSynthesis.cancel();
    setSpeaking(prev => Object.keys(prev).reduce((acc, key) => ({
      ...acc,
      [key]: false
    }), {}));

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Get available voices and select a natural-sounding one
    const voices = window.speechSynthesis.getVoices();
    // Try to find a good English voice, prioritizing specific voices
    const preferredVoice = voices.find(voice => 
      voice.name.includes('Google UK English Male') && voice.lang.startsWith('en')
    ) || voices.find(voice => 
      voice.name.includes('Daniel') && voice.lang.includes('GB')
    ) || voices.find(voice => 
      (voice.name.includes('Microsoft David') ||
       voice.name.includes('Microsoft Zira')) &&
      voice.lang.startsWith('en')
    ) || voices.find(voice => voice.lang.startsWith('en')); // Fallback to any English voice

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    // Use the custom speed or default to 1
    utterance.rate = voiceSpeed[index] || 1;
    utterance.pitch = 1;
    utterance.onend = () => {
      setSpeaking(prev => ({ ...prev, [index]: false }));
    };
    utterance.onerror = () => {
      setSpeaking(prev => ({ ...prev, [index]: false }));
    };

    setSpeaking(prev => ({ ...prev, [index]: true }));
    window.speechSynthesis.speak(utterance);
  };

  // Add a useEffect to handle voice loading
  useEffect(() => {
    // Load voices when component mounts
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
    };
    
    loadVoices();
    
    // Chrome requires this event listener
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const parseItinerary = (text: string): TourResponse | null => {
    try {
      const jsonResponse = JSON.parse(text);

      const parsedResponse = {
        tourTitle: jsonResponse.TourTitle || `${city} Walking Tour`,
        duration: jsonResponse.Duration || `${duration} hours`,
        tourDetails: jsonResponse.TourDetails.map((detail: any) => {
          // Validate coordinates
          if (!detail.Coordinates || typeof detail.Coordinates.lat !== 'number' || typeof detail.Coordinates.lng !== 'number') {
            return {
              pointOfInterest: detail.PointOfInterest,
              location: detail.Location,
              description: detail.Description,
              timeSpent: detail.TimeSpent,
              walkingDirections: detail.WalkingDirections,
              funFacts: Array.isArray(detail["Fun Facts"]) ? detail["Fun Facts"] : [detail["Fun Facts"]].filter(Boolean),
              coordinates: null
            };
          }

          return {
            pointOfInterest: detail.PointOfInterest,
            location: detail.Location,
            description: detail.Description,
            timeSpent: detail.TimeSpent,
            walkingDirections: detail.WalkingDirections,
            funFacts: Array.isArray(detail["Fun Facts"]) ? detail["Fun Facts"] : [detail["Fun Facts"]].filter(Boolean),
            coordinates: {
              lat: parseFloat(detail.Coordinates.lat),
              lng: parseFloat(detail.Coordinates.lng)
            }
          };
        })
      };

      return parsedResponse;
    } catch (error) {
      return null;
    }
  };

  const generateTour = async () => {
    if (!city || !duration || !selectedTheme) {
      setError('Please fill in all fields');
      return;
    }

    const durationNum = parseFloat(duration);
    if (isNaN(durationNum) || durationNum < 0.5 || durationNum > 12) {
      setError('Duration must be between 0.5 and 12 hours');
      return;
    }

    setLoading(true);
    setError(null);
    setItinerary(null);
    setTourSaved(false);

    try {
      // console.log('Calling generateWalkingTour with:', { city, duration: durationNum, theme: selectedTheme });
      const generateWalkingTour = httpsCallable(functions, 'generateWalkingTour', {
        timeout: 540000, // 9 minutes — must match Cloud Function timeout
      });
      const result = await generateWalkingTour({
        city,
        duration: durationNum,
        theme: selectedTheme
      });
      // console.log('Received result from generateWalkingTour:', result);
      // The backend now returns the tour data directly, no need to parse JSON string
      const tourData = result.data as {
        TourTitle: string;
        Duration: string;
        TourDetails: Array<{
          PointOfInterest: string;
          Location: string | {
            placeName?: string;
            streetAddress?: string;
            city?: string;
            state?: string;
            country?: string;
          };
          Description: string;
          TimeSpent: string;
          WalkingDirections: string;
          "Fun Facts": string[];
        }>;
      };

      const parsedItinerary = sanitizeTourResponse({
        tourTitle: tourData.TourTitle,
        duration: tourData.Duration,
        tourDetails: tourData.TourDetails.map((detail) => {
          const parsedLocation = parseTourLocation(detail.Location, detail.PointOfInterest, city);

          return {
            pointOfInterest: detail.PointOfInterest,
            location: parsedLocation.location,
            locationDetails: parsedLocation.locationDetails,
            description: detail.Description,
            timeSpent: detail.TimeSpent,
            walkingDirections: detail.WalkingDirections,
            funFacts: detail["Fun Facts"],
            coordinates: null,
          };
        }),
      }, city);

      shouldScrollToTourRef.current = true;
      setItinerary(parsedItinerary);
      setTourSaved(false);
      setLoading(false);

      const runId = ++enrichmentRunId.current;
      void enrichTourDetails(parsedItinerary.tourDetails, city, runId);
    } catch (err: any) {
      console.error('Error calling generateWalkingTour:', err);
      if (err.code) {
        console.error('Firebase error code:', err.code);
      }
      if (err.message) {
        console.error('Firebase error message:', err.message);
      }
      setError('Failed to generate tour. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSubmit = async (index: number, question: string) => {
    if (!question.trim()) return;

    setLoadingAnswers(prev => ({ ...prev, [index]: true }));
    try {
      const askQuestion = httpsCallable(functions, 'askLocationQuestion');
      const result = await askQuestion({
        location: itinerary?.tourDetails[index].pointOfInterest,
        context: itinerary?.tourDetails[index].description,
        question: question
      });

      setAnswers(prev => ({
        ...prev,
        [index]: formatPlainTextAnswer((result.data as { answer: string }).answer)
      }));
      setQuestions(prev => ({ ...prev, [index]: '' }));
    } catch (err) {
      console.error('Error asking question:', err);
      setError('Failed to get answer. Please try again.');
    } finally {
      setLoadingAnswers(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSaveTour = async () => {
    if (!user || !itinerary || tourSaved || savingTour) return;

    setSavingTour(true);
    try {
      await saveTourForUser(user.uid, itinerary, { city, theme: selectedTheme });
      setTourSaved(true);
    } catch (err) {
      console.error('Error saving tour:', err);
      setError('Failed to save tour. Please try again.');
    } finally {
      setSavingTour(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim() || submittingFeedback) return;

    setSubmittingFeedback(true);
    try {
      const submitFeedback = httpsCallable(functions, 'submitFeedback');
      await submitFeedback({
        message: feedback,
        tourCity: city || null,
        tourTitle: itinerary?.tourTitle || null,
      });

      setFeedback('');
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleStopSelect = useCallback((index: number) => {
    setSelectedStop(index);
    // Scroll to the selected stop
    const element = document.getElementById(`stop-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleCloseWelcomePopup = () => {
    localStorage.setItem('hasVisitedWTG', 'true');
    setShowWelcomePopup(false);
  };

  const enrichTourDetails = useCallback(async (
    tourDetails: TourResponse['tourDetails'],
    tourCity: string,
    runId: number
  ) => {
    setEnrichingTour(true);

    try {
      await waitForGoogleMaps();
      const cityContext = await resolveTourCityContext(tourCity);
      const enrichedDetails = await Promise.all(
        tourDetails.map(async (detail) => {
          const normalizedDetail = sanitizeTourResponse({
            tourTitle: '',
            duration: '',
            tourDetails: [detail],
          }, tourCity).tourDetails[0];

          const [coordinates, thumbnailUrl] = await Promise.all([
            resolveStopCoordinates(normalizedDetail, tourCity, cityContext),
            fetchWikipediaImage(normalizedDetail.pointOfInterest, tourCity),
          ]);

          return {
            ...normalizedDetail,
            coordinates,
            thumbnailUrl: thumbnailUrl || undefined,
          };
        })
      );

      if (enrichmentRunId.current !== runId) {
        return;
      }

      setItinerary((prev) => (
        prev ? { ...prev, tourDetails: enrichedDetails } : prev
      ));
    } finally {
      if (enrichmentRunId.current === runId) {
        setEnrichingTour(false);
      }
    }
  }, []);

  const handleSampleTourChange = (event: SelectChangeEvent<string>) => {
    const selectedTitle = event.target.value as string;
    setSelectedSampleTour(selectedTitle);

    if (selectedTitle === '') {
      // If default option is selected, clear itinerary and navigate to root
      setItinerary(null);
      setCity('');
      setDuration('');
      setSelectedTheme('');
      setError(null);
      setTourSaved(false);
      navigate('/');
    } else {
      // Find the tour object matching the selected displayTitle
      const selectedTour = sampleTours.find(tour => tour.displayTitle === selectedTitle);
      if (selectedTour) {
        // Load the itinerary from the selected sample tour
        setItinerary(selectedTour.itinerary as TourResponse);
        setTourSaved(false);
        // Clear inputs
        setCity('');
        setDuration('');
        setSelectedTheme('');
        setError(null);
        setLoading(false); 
        navigate(`/tour/${selectedTour.slug}`);
      }
    }
  };

  const handleOpenDialog = (pageKey: DialogPageKey) => {
    if (!pageKey) return;
    let title = '';
    switch (pageKey) {
      case 'about': title = 'About Us'; break;
      case 'privacy': title = 'Privacy Policy'; break;
      case 'terms': title = 'Terms of Service'; break;
    }
    setDialogTitle(title);
    setDialogPageKey(pageKey);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Optional: Reset title/key after closing animation
    setTimeout(() => {
        setDialogTitle('');
        setDialogPageKey(null);
    }, 300); // Adjust timeout to match dialog close animation
  };

  const renderDialogContent = () => {
    switch (dialogPageKey) {
      case 'about': return <About />;
      case 'privacy': return <PrivacyPolicy />;
      case 'terms': return <TermsOfService />;
      default: return null;
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        bgcolor: '#FFF5F5',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowX: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(
              ${THEME.colors.secondary}30 2px, 
              transparent 2px
            ),
            linear-gradient(
              90deg,
              ${THEME.colors.secondary}30 2px, 
              transparent 2px
            )`,
          backgroundSize: '50px 50px',
          backgroundPosition: '0px 0px',
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 245, 245, 0.5)',
          pointerEvents: 'none',
          zIndex: 0,
        }
      }}
    >
      <AppHeader />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          bgcolor: 'white',
          height: { xs: '300px', sm: '400px' },
          mt: { xs: '-40px', sm: '-50px' }
        }}
      >
        <Container maxWidth="lg" sx={{ 
          py: 0, 
          position: 'relative', 
          zIndex: 1,
          mt: { xs: '60px', sm: '75px' }
        }}>
          <Box sx={{ 
            maxWidth: 900, 
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 4 }
          }}>
            <Box sx={{ 
              width: '100%',
              height: { xs: '200px', sm: '300px' },
              mt: { xs: '-10px', sm: 0 },
              maxWidth: { xs: '100%', sm: '500px' },
              mx: 'auto'
            }}>
              <img 
                src="/images/cover-image.png" 
                alt="Walking Tour Cover"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ 
        py: { xs: 2, sm: 4 }, 
        position: 'relative', 
        zIndex: 1,
        flex: 1
      }}>
        <Box sx={{ 
          maxWidth: 900, 
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, sm: 4 }
        }}>
          <Card 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 4 },
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
              position: 'relative',
              overflow: 'hidden',
              mt: { xs: '-100px', sm: '-100px' },
              zIndex: 2
            }}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 4 } }}>
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: '#363636',
                  mb: { xs: 2, sm: 3 },
                  letterSpacing: '-0.5px',
                  fontSize: { xs: '1.75rem', sm: '2.125rem' }
                }}
              >
                Create Your Perfect Walking Tour
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: { xs: 2, sm: 4 },
              flexDirection: { xs: 'column', sm: 'row' } 
            }}>
              <TextField
                fullWidth
                label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter a city"
                variant="outlined"
                disabled={loading}
                InputProps={{
                  startAdornment: <ExploreIcon sx={{ color: '#4ECDC4', mr: 1 }} />
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                      }
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ECDC4',
                        borderWidth: 1
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderWidth: 1
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#4ECDC4'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    backgroundColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 30px white inset !important',
                    WebkitTextFillColor: 'inherit !important',
                    '&::selection': {
                      backgroundColor: 'rgba(78, 205, 196, 0.1)'
                    }
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 30px white inset !important',
                    WebkitTextFillColor: 'inherit !important',
                    backgroundColor: 'white !important'
                  }
                }}
              />
              <TextField
                fullWidth
                label="Duration (hours)"
                type="number"
                value={duration}
                onChange={(e) => {
                  const value = e.target.value;
                  setDuration(value);

                  const parsed = parseFloat(value);
                  if (value !== '' && (parsed <= 0 || parsed > 12)) {
                    setDurationError('Duration must be between 0.5 and 12 hours');
                  } else {
                    setDurationError('');
                  }
                }}
                placeholder="How many hours?"
                variant="outlined"
                error={!!durationError}
                helperText={durationError}
                disabled={loading}
                InputProps={{
                  startAdornment: <AccessTimeIcon sx={{ color: '#FF6B6B', mr: 1 }} />,
                  inputProps: {
                    min: 0.5,
                    max: 12,
                    step: 0.5
                  }
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f8f9fa'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF6B6B',
                        borderWidth: 2
                      }
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#FF6B6B'
                  }
                }}
              />
            </Box>

            <FormControl 
              fullWidth 
              disabled={loading}
              sx={{ 
                mb: { xs: 2, sm: 4 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f8f9fa'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ECDC4',
                      borderWidth: 2
                    }
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ECDC4'
                }
              }}
            >
              <InputLabel id="theme-label">Theme</InputLabel>
              <Select
                labelId="theme-label"
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                label="Theme"
                startAdornment={<CategoryIcon sx={{ color: '#4ECDC4', mr: 1 }} />}
                renderValue={(value) => {
                  const selectedTheme = TOUR_THEMES.find(theme => theme.value === value);
                  return selectedTheme ? selectedTheme.label : '';
                }}
              >
                {TOUR_THEMES.map((tourTheme) => (
                  <MenuItem 
                    key={tourTheme.value} 
                    value={tourTheme.value}
                    sx={{
                      py: 1.5,
                      whiteSpace: 'normal',
                      '&:hover': {
                        backgroundColor: 'rgba(78, 205, 196, 0.08)'
                      }
                    }}
                  >
                    <Box sx={{ 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {tourTheme.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          lineHeight: 1.4
                        }}
                      >
                        {tourTheme.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={generateTour}
              disabled={loading || !!durationError || !city || !selectedTheme}
              fullWidth
              size="large"
              sx={{ 
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#4ECDC4',
                transition: 'all 0.2s ease',
                textTransform: 'none',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#45b8b0',
                  boxShadow: '0 4px 12px rgba(78, 205, 196, 0.2)'
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(78, 205, 196, 0.5)',
                  color: 'white'
                }
              }}
            >
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  position: 'relative',
                  width: '100%',
                  justifyContent: 'center',
                  height: { xs: '32px', sm: '40px' }
                }}>
                  <Typography 
                    sx={{ 
                      color: 'white',
                      fontSize: { xs: '0.875rem', sm: '1.1rem' },
                      fontWeight: 600,
                      animation: `${textAnimation} 3s infinite ease-in-out`,
                      textAlign: 'center'
                    }}
                  >
                    {loadingTexts[loadingTextIndex]}
                  </Typography>
                </Box>
              ) : (
                'Generate Tour'
              )}
            </Button>

            <Divider sx={{ my: { xs: 1, sm: 2 } }}>
              <Typography variant="overline">OR</Typography>
            </Divider>

            <Box>
              <FormControl 
                fullWidth 
                disabled={loading}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2, 
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME.colors.secondary,
                      borderWidth: '2px'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME.colors.secondary,
                      borderWidth: '2px'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME.colors.secondary,
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: THEME.colors.secondary }
                }}
              >
                <InputLabel id="sample-tour-label" shrink={true}>Explore curated tours</InputLabel>
                <Select
                  labelId="sample-tour-label"
                  value={selectedSampleTour}
                  onChange={handleSampleTourChange}
                  label="Explore curated tours"
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === "") {
                      // Wrap icon and text in a flex Box
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: muiTheme.palette.text.secondary }}>
                          <StarsIcon sx={{ color: THEME.colors.primary, mr: 1, fontSize: '1.25rem' }} /> 
                          <span>Select a city</span>
                        </Box>
                      );
                    }
                    // Otherwise, return the selected displayTitle
                    return selected;
                  }}
                >
                  {sortedSampleTours.map((tour) => (
                    <MenuItem key={tour.displayTitle} value={tour.displayTitle}>
                      {tour.displayTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {error && (
              <Typography 
                color="error" 
                sx={{ 
                  mt: 2,
                  textAlign: 'center',
                  bgcolor: '#FFF0F0',
                  color: '#FF6B6B',
                  p: 2,
                  borderRadius: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {error}
              </Typography>
            )}
          </Card>

          {itinerary && (
            <Card 
              ref={tourResultsRef}
              elevation={0} 
              sx={{ 
                borderRadius: { xs: 0, sm: 6 },
                overflow: 'hidden',
                background: 'white',
                mt: { xs: 4, sm: 6 }
              }}
            >
              <CardContent sx={{ 
                p: { xs: 2, sm: 4 },
                position: 'relative',
                zIndex: 1
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      textAlign: 'center',
                      mb: 0,
                      color: THEME.colors.text,
                      fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}
                  >
                    {itinerary.tourTitle}
                  </Typography>
                  {user && (
                    <Button
                      variant={tourSaved ? 'outlined' : 'contained'}
                      onClick={handleSaveTour}
                      disabled={tourSaved || savingTour}
                      startIcon={
                        savingTour ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : tourSaved ? (
                          <CheckIcon />
                        ) : (
                          <AddIcon />
                        )
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        flexShrink: 0,
                        bgcolor: tourSaved ? 'transparent' : THEME.colors.primary,
                        borderColor: THEME.colors.primary,
                        color: tourSaved ? THEME.colors.primary : 'white',
                        px: { xs: 2, sm: 2.5 },
                        height: { xs: 40, sm: 44 },
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: tourSaved ? `${THEME.colors.primary}11` : '#ff5252',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                          color: THEME.colors.primary,
                          borderColor: THEME.colors.primary,
                          opacity: 0.7,
                        },
                      }}
                    >
                      {tourSaved ? 'Saved' : 'Save'}
                    </Button>
                  )}
                </Box>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{ 
                    textAlign: 'center',
                    mb: { xs: 2, sm: 4 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: THEME.colors.text,
                    opacity: 0.8,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  <AccessTimeIcon sx={{ color: THEME.colors.secondary }} /> 
                  Duration: {itinerary.duration}
                </Typography>

                {enrichingTour && (
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      mb: 2,
                      color: THEME.colors.secondary,
                      fontWeight: 500,
                    }}
                  >
                    Loading map pins and photos...
                  </Typography>
                )}
                
                <Box sx={{ 
                  height: { xs: '300px', sm: '500px' }, 
                  mb: { xs: 2, sm: 3 }, 
                  borderRadius: 3, 
                  overflow: 'hidden' 
                }}>
                  <TourMap
                    tourStops={itinerary.tourDetails.map(detail => ({
                      pointOfInterest: detail.pointOfInterest,
                      coordinates: detail.coordinates
                    }))}
                    selectedStop={selectedStop}
                    onStopSelect={handleStopSelect}
                  />
                </Box>

                <List sx={{ mt: 2 }}>
                  {itinerary.tourDetails.map((detail, index) => (
                    <React.Fragment key={index}>
                      <Accordion 
                        defaultExpanded={index === 0}
                        sx={{
                          mb: 2,
                          border: 'none',
                          '&:before': {
                            display: 'none',
                          },
                          background: 'transparent',
                          boxShadow: 'none'
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon sx={{ color: THEME.colors.primary }} />}
                          sx={{
                            background: THEME.colors.accent3,
                            borderRadius: 3,
                            '&:hover': {
                              background: THEME.colors.accent4,
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 1, sm: 2 },
                            width: '100%'
                          }}>
                            <Box sx={{
                              bgcolor: THEME.colors.primary,
                              color: 'white',
                              width: { xs: 28, sm: 36 },
                              height: { xs: 28, sm: 36 },
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: { xs: '1rem', sm: '1.2rem' }
                            }}>
                              {index + 1}
                            </Box>
                            <Typography variant="h6" sx={{ 
                              flex: 1,
                              color: THEME.colors.text,
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                              {detail.pointOfInterest}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ 
                          p: { xs: 2, sm: 3 },
                          background: '#FAFAFA',
                          borderRadius: '0 0 16px 16px'
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: THEME.colors.secondary
                            }}>
                              <LocationOnIcon />
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {detail.location}
                              </Typography>
                            </Box>

                            <Box sx={{ 
                              display: 'flex',
                              gap: 2,
                              alignItems: 'flex-start',
                              bgcolor: 'white',
                              p: { xs: 1, sm: 2 },
                              borderRadius: 2,
                              flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'row', sm: 'column' }, 
                                gap: 1,
                                alignItems: 'center',
                                minWidth: { xs: 'auto', sm: '60px' },
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'space-between', sm: 'flex-start' }
                              }}>
                                <IconButton
                                  onClick={() => handleSpeak(index, detail.description)}
                                  sx={{
                                    bgcolor: speaking[index] ? THEME.colors.primary : THEME.colors.secondary,
                                    color: 'white',
                                    '&:hover': {
                                      bgcolor: speaking[index] ? THEME.colors.primary : THEME.colors.secondary,
                                      opacity: 0.9
                                    },
                                    width: { xs: 32, sm: 36 },
                                    height: { xs: 32, sm: 36 },
                                    flexShrink: 0
                                  }}
                                >
                                  {speaking[index] ? <StopIcon /> : <PlayArrowIcon />}
                                </IconButton>
                                <Box sx={{ 
                                  display: { xs: 'flex', sm: 'block' },
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      mb: { xs: 0, sm: 0.5 }, 
                                      textAlign: 'center',
                                      fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    }}
                                  >
                                    {voiceSpeed[index]?.toFixed(1) || '1.0'}x
                                  </Typography>
                                  <Slider
                                    orientation={window.innerWidth < 600 ? "horizontal" : "vertical"}
                                    size="small"
                                    value={voiceSpeed[index] || 1}
                                    min={0.5}
                                    max={2}
                                    step={0.1}
                                    onChange={(_, value) => setVoiceSpeed(prev => ({ ...prev, [index]: value as number }))}
                                    sx={{
                                      color: THEME.colors.secondary,
                                      height: { xs: 4, sm: 80 },
                                      width: { xs: '100px', sm: 'auto' },
                                      '& .MuiSlider-thumb': {
                                        width: { xs: 12, sm: 12 },
                                        height: { xs: 12, sm: 12 },
                                      },
                                      '& .MuiSlider-track': {
                                        width: { xs: 'auto', sm: 4 },
                                      },
                                      '& .MuiSlider-rail': {
                                        width: { xs: 'auto', sm: 4 },
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ 
                                flex: 1, 
                                display: 'flex', 
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' }
                              }}>
                                {detail.thumbnailUrl && (
                                  <Box sx={{ 
                                    width: { xs: '100%', sm: '200px' }, 
                                    height: { xs: '150px', sm: '150px' }, 
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    order: { xs: 1, sm: 2 }
                                  }}>
                                    <img 
                                      src={detail.thumbnailUrl} 
                                      alt={detail.pointOfInterest}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                      }}
                                    />
                                  </Box>
                                )}
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    lineHeight: { xs: 1.4, sm: 1.8 },
                                    color: THEME.colors.text,
                                    textAlign: 'justify',
                                    flex: 1,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    order: { xs: 2, sm: 1 }
                                  }}
                                >
                                  {detail.description}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: THEME.colors.primary
                            }}>
                              <AccessTimeIcon />
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Time at location: {detail.timeSpent}
                              </Typography>
                            </Box>

                            {detail.walkingDirections && (
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 2,
                                bgcolor: THEME.colors.accent2,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 3,
                                color: 'white'
                              }}>
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {detail.walkingDirections}
                                </Typography>
                              </Box>
                            )}

                            {detail.funFacts && detail.funFacts.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: THEME.colors.primary,
                                    fontWeight: 600,
                                    mb: 2,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  <EmojiObjectsIcon /> Fun Facts
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap', 
                                  gap: 1,
                                  mt: 1
                                }}>
                                  {detail.funFacts.map((fact, factIndex) => (
                                    <Chip
                                      key={factIndex}
                                      label={fact}
                                      sx={{ 
                                        bgcolor: THEME.colors.accent1,
                                        color: THEME.colors.text,
                                        p: 1,
                                        '& .MuiChip-label': {
                                          px: 2,
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                          whiteSpace: 'normal',
                                          maxWidth: { xs: '100%', sm: '200px' },
                                          textAlign: 'left'
                                        },
                                        height: 'auto',
                                        minHeight: { xs: '32px', sm: '32px' },
                                        fontWeight: 500,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            <Box sx={{ 
                              mt: { xs: 2, sm: 3 }, 
                              pt: { xs: 2, sm: 3 }, 
                              borderTop: `1px solid ${THEME.colors.accent3}` 
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                gutterBottom
                                sx={{ 
                                  color: THEME.colors.primary,
                                  fontWeight: 600,
                                  mb: 2,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                              >
                                Ask a Question
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 2,
                                alignItems: 'center',
                                flexDirection: { xs: 'column', sm: 'row' }
                              }}>
                                <TextField
                                  fullWidth
                                  placeholder="What would you like to know about this location?"
                                  value={questions[index] || ''}
                                  onChange={(e) => setQuestions(prev => ({
                                    ...prev,
                                    [index]: e.target.value
                                  }))}
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: 'white',
                                      height: { xs: '48px', sm: '56px' }
                                    }
                                  }}
                                />
                                <Button
                                  variant="contained"
                                  onClick={() => handleQuestionSubmit(index, questions[index] || '')}
                                  disabled={loadingAnswers[index] || !questions[index]?.trim()}
                                  sx={{
                                    bgcolor: THEME.colors.secondary,
                                    minWidth: { xs: '100%', sm: '120px' },
                                    height: { xs: '48px', sm: '56px' },
                                    '&:hover': {
                                      bgcolor: THEME.colors.secondary,
                                      opacity: 0.9
                                    }
                                  }}
                                >
                                  {loadingAnswers[index] ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : (
                                    'Ask'
                                  )}
                                </Button>
                              </Box>
                              {answers[index] && (
                                <Box sx={{ 
                                  mt: 2,
                                  p: 2,
                                  bgcolor: 'white',
                                  borderRadius: 2,
                                  border: `1px solid ${THEME.colors.accent3}`
                                }}>
                                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'pre-line' }}>
                                    {formatPlainTextAnswer(answers[index])}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </React.Fragment>
                  ))}
                </List>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center',
                    mt: 2,
                    fontSize: '1.0rem',
                    display: 'block'
                  }}
                >
                  thanks for walking with us :)
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center',
                    mt: 2, 
                    fontSize: '1.0rem', 
                    display: 'block'
                  }}
                >
                  questions or comments?
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  mt: 1.5,
                  width: '100%',
                }}>
                  <TextField
                    fullWidth
                    placeholder="Share a question or comment..."
                    value={feedback}
                    onChange={(e) => {
                      setFeedback(e.target.value);
                      if (feedbackSubmitted) setFeedbackSubmitted(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleFeedbackSubmit();
                      }
                    }}
                    variant="outlined"
                    disabled={submittingFeedback}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                        height: { xs: '48px', sm: '56px' }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleFeedbackSubmit}
                    disabled={submittingFeedback || !feedback.trim()}
                    sx={{
                      bgcolor: THEME.colors.secondary,
                      minWidth: { xs: '100%', sm: '120px' },
                      height: { xs: '48px', sm: '56px' },
                      '&:hover': {
                        bgcolor: THEME.colors.secondary,
                        opacity: 0.9
                      }
                    }}
                  >
                    {submittingFeedback ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Send'
                    )}
                  </Button>
                </Box>
                {feedbackSubmitted && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME.colors.secondary,
                      textAlign: 'center',
                      mt: 1,
                      fontSize: '0.95rem',
                      display: 'block'
                    }}
                  >
                    thanks for your feedback!
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Container>

      <Footer onLinkClick={handleOpenDialog} />

      <WelcomePopup
        open={showWelcomePopup}
        onClose={handleCloseWelcomePopup}
      />
      
      <InfoDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={dialogTitle}
      >
        {renderDialogContent()}
      </InfoDialog>
    </Box>
  );
};

export default TourForm; 